import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useKeycloak } from '@react-keycloak/ssr';
import type { OrganizationFeatures, Organization, OrganizationSetting } from '@/types/organization';
import { useOrganizationMembership } from '@/session/OrganizationEntryGuard';
import { asOrganizationId } from '@/session/identity';
import { useRouteOrganization } from '@/session/OrganizationIdentityContext';
import { acquireOperationContext, operationRequirement } from '@/session/operation';
import { CACHE_NAMESPACES, readCache } from '@/session/cacheKeys';
import { updateCurrentOrganizationTimezoneCache } from '@/session/organizationTimezoneCache';
import { useEndpoints } from '@/session/useEndpoints';
import { useOperationRuns } from '@/session/useOperationRuns';

export type OrganizationTimezoneState =
  | { status: 'loading'; displayTimezone: string }
  | { status: 'ready'; timezone: string; displayTimezone: string }
  | { status: 'missing'; displayTimezone: string }
  | { status: 'error'; displayTimezone: string; error: Error };

export type OrganizationContextType = {
  data: OrganizationFeatures | null;
  organization: Organization | null;
  timezone: string;
  isOrgTimezone: boolean;
  timezoneState: OrganizationTimezoneState;
  retryTimezone: () => void;
  hasFeature: (featureName: string) => boolean;
  hasTag: (tagName: string) => boolean;
};

const browserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

const initialState: OrganizationContextType = {
  data: null,
  organization: null,
  timezone: browserTimezone(),
  isOrgTimezone: false,
  timezoneState: { status: 'loading', displayTimezone: browserTimezone() },
  retryTimezone: () => {},
  hasFeature: () => false,
  hasTag: () => false,
};

export const OrganizationContext = createContext<OrganizationContextType>(initialState);

export const OrganizationProvider = ({ children }: { children: React.ReactNode }) => {
  const [data, setData] = useState<OrganizationFeatures | null>(null);
  const [timezoneState, setTimezoneState] = useState<OrganizationTimezoneState>(() => ({
    status: 'loading',
    displayTimezone: browserTimezone(),
  }));
  const [timezoneRetryNonce, setTimezoneRetryNonce] = useState(0);
  const { keycloak, initialized } = useKeycloak();
  const routeOrganization = useRouteOrganization();
  const { organization } = useOrganizationMembership();
  const endpoints = useEndpoints();
  const operationRuns = useOperationRuns();

  const retryTimezone = useCallback(() => setTimezoneRetryNonce((value) => value + 1), []);

  useEffect(() => {
    if (!initialized || !keycloak?.authenticated || !routeOrganization || !organization || organization.name !== routeOrganization) {
      return;
    }

    const organizationId = asOrganizationId(routeOrganization);
    const operation = acquireOperationContext(organizationId);
    if (!operation.isScopeCurrent()) return;
    const run = operationRuns.begin('organization-features', operation);
    run.publish(() => setData(null));

    void endpoints.organizationData
      .getOrganizationFeatures({
        signal: operation.scopeSignal,
        operationRequirement: operationRequirement(operation, 'scope'),
      })
      .then((response) => {
        run.publish(() => {
          if (response.isError) {
            setData({ organization_name: organizationId, enabled_features: [], subscriptions: {} });
          } else {
            setData(response.data);
          }
        });
      });

    return () => run.invalidate();
  }, [endpoints, initialized, keycloak?.authenticated, operationRuns, organization, routeOrganization]);

  useEffect(() => {
    if (!initialized || !keycloak?.authenticated || !routeOrganization || !organization || organization.name !== routeOrganization) {
      return;
    }

    const organizationId = asOrganizationId(routeOrganization);
    const operation = acquireOperationContext(organizationId);
    if (!operation.isScopeCurrent()) return;
    const run = operationRuns.begin('organization-timezone', operation);
    const displayTimezone = browserTimezone();
    run.publish(() => setTimezoneState({ status: 'loading', displayTimezone }));

    const cachedTimezone = readCache<string>({
      scope: 'userOrg',
      namespace: CACHE_NAMESPACES.organizationTimezone,
      storage: 'sessionStorage',
      organization: organizationId,
      subject: operation.subject,
      validate: (value) => typeof value === 'string' && value.length > 0,
    });
    if (cachedTimezone) {
      run.publish(() => setTimezoneState({ status: 'ready', timezone: cachedTimezone, displayTimezone: cachedTimezone }));
      return () => run.invalidate();
    }

    void endpoints.organizationData
      .getOrganizationSettings(
        { limit: 1000 },
        {
          operationRequirement: operationRequirement(operation, 'scope'),
        }
      )
      .then((response) => {
        run.publish(() => {
          if (response.isError) {
            setTimezoneState({
              status: 'error',
              displayTimezone,
              error: new Error(response.errors.error || 'Unable to load organization timezone'),
            });
            return;
          }
          const setting = response.result.find((candidate: OrganizationSetting) => ['time_zone', 'timezone'].includes(candidate.name));
          if (!setting?.value) {
            setTimezoneState({ status: 'missing', displayTimezone });
            return;
          }
          updateCurrentOrganizationTimezoneCache(setting.value, organizationId, operation.subject);
          setTimezoneState({ status: 'ready', timezone: setting.value, displayTimezone: setting.value });
        });
      });

    return () => run.invalidate();
  }, [endpoints, initialized, keycloak?.authenticated, operationRuns, organization, routeOrganization, timezoneRetryNonce]);

  const timezone = timezoneState.status === 'ready' ? timezoneState.timezone : timezoneState.displayTimezone;
  const isOrgTimezone = timezoneState.status === 'ready';

  const hasFeature = useCallback((featureName: string): boolean => data?.enabled_features?.includes(featureName) || false, [data]);
  const hasTag = useCallback(
    (tagName: string): boolean => {
      if (!organization?.tags) return false;
      const normalizedTag = tagName.toLowerCase();
      return organization.tags.some((tag) => tag.toLowerCase() === normalizedTag);
    },
    [organization]
  );

  const value = useMemo(
    () => ({ data, organization, timezone, isOrgTimezone, timezoneState, retryTimezone, hasFeature, hasTag }),
    [data, organization, timezone, isOrgTimezone, timezoneState, retryTimezone, hasFeature, hasTag]
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};

export const useOrganizationData = () => useContext(OrganizationContext);
